import { useCallback } from 'react';
import { toast } from 'sonner';
import { StoreCore } from '../types/store';
import { WeekRow } from '../types/shared';
import { DEMO_DATE, WEEK_ID } from '../data/demo';
import { copy } from '../data/copy';
import { uid, upsertTx } from '../utils/db';

export function useRewardActions(core: StoreCore, balanceOf: (memberId: string) => number) {
  const { db, setDb, family, viewer, isAdmin } = core;

  const requestReward = useCallback(
    (rewardId: string, slot: string) => {
      const reward = family.rewards.find((r) => r.id === rewardId);
      if (!reward || viewer.role !== 'otrok') return;
      setDb((prev) => ({
        ...prev,
        rewardRequests: [
          {
            id: uid('rr'),
            familyId: family.id,
            memberId: viewer.id,
            rewardId,
            title: reward.title,
            cost: reward.cost,
            slot,
            status: 'caka',
            createdAt: Date.now(),
          },
          ...prev.rewardRequests,
        ],
      }));
      toast.success('Zahteva poslana staršema. Točke se odštejejo šele ob odobritvi.');
    },
    [family, viewer, setDb]
  );

  const cancelReward = useCallback(
    (id: string) =>
      setDb((prev) => ({
        ...prev,
        rewardRequests: prev.rewardRequests.filter((r) => !(r.id === id && r.memberId === viewer.id && r.status === 'caka')),
      })),
    [setDb, viewer.id]
  );

  /** Bonus se odšteje enkrat – ob odobritvi. Ponovna odobritev iste zahteve ne odšteje znova. */
  const approveReward = useCallback(
    (id: string, override: boolean): boolean => {
      const req = db.rewardRequests.find((r) => r.id === id);
      if (!req || !isAdmin || req.status !== 'caka') return false;
      const eligible = family.lastWeekReached[req.memberId] || override;
      if (!eligible) {
        toast.error('Pogoj praga v zadnjem zaključenem tednu ni izpolnjen. Označi izjemo po presoji.');
        return false;
      }
      if (balanceOf(req.memberId) < req.cost) {
        toast.error('Premalo goodies za to nagrado.');
        return false;
      }
      setDb((prev) => {
        const next = upsertTx(prev, {
          id: `reward:${id}`,
          familyId: family.id,
          memberId: req.memberId,
          amount: -req.cost,
          reason: `Nagrada: ${req.title}`,
          date: DEMO_DATE,
        });
        return {
          ...next,
          rewardRequests: next.rewardRequests.map((r) => (r.id === id ? { ...r, status: 'odobreno', decidedBy: viewer.id, override } : r)),
        };
      });
      toast.success(copy.rewardApproved);
      return true;
    },
    [db.rewardRequests, isAdmin, family, balanceOf, setDb, viewer.id]
  );

  const rejectReward = useCallback(
    (id: string, note: string) =>
      setDb((prev) => ({
        ...prev,
        rewardRequests: prev.rewardRequests.map((r) =>
          r.id === id && r.status === 'caka' ? { ...r, status: 'zavrnjeno', decidedBy: viewer.id, note } : r
        ),
      })),
    [setDb, viewer.id]
  );

  /** Samo zapis koriščenja – brez ponovnega odštevanja. */
  const redeemReward = useCallback(
    (id: string) => {
      setDb((prev) => ({
        ...prev,
        rewardRequests: prev.rewardRequests.map((r) => (r.id === id && r.status === 'odobreno' ? { ...r, status: 'koristeno' } : r)),
      }));
      toast.success('Koriščenje zabeleženo.');
    },
    [setDb]
  );

  const draftKey = (memberId: string) => `${family.id}:${WEEK_ID}:${memberId}`;

  const setWeekDraft = useCallback(
    (memberId: string, patch: { paid?: boolean; override?: boolean }) =>
      setDb((prev) => {
        const key = `${family.id}:${WEEK_ID}:${memberId}`;
        const cur = prev.weekDraft[key] ?? {};
        if (cur.paid && patch.paid !== undefined) return prev; // izplačilo ne more biti dvojno ali preklicano
        return { ...prev, weekDraft: { ...prev.weekDraft, [key]: { ...cur, ...patch } } };
      }),
    [setDb, family.id]
  );

  const closeWeek = useCallback(
    (rows: WeekRow[]) => {
      if (!isAdmin) return;
      setDb((prev) => {
        if (prev.weekCloses.some((w) => w.familyId === family.id && w.weekId === WEEK_ID)) return prev;
        return { ...prev, weekCloses: [...prev.weekCloses, { familyId: family.id, weekId: WEEK_ID, rows, closedBy: viewer.id, closedAt: Date.now() }] };
      });
      toast.success(copy.weekClosed);
    },
    [isAdmin, setDb, family.id, viewer.id]
  );

  const weekDraftFor = (memberId: string) => db.weekDraft[draftKey(memberId)] ?? {};
  const weekClose = db.weekCloses.find((w) => w.familyId === family.id && w.weekId === WEEK_ID);

  return { requestReward, cancelReward, approveReward, rejectReward, redeemReward, setWeekDraft, closeWeek, weekDraftFor, weekClose };
}
